import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Textarea
} from '@bliks-io/ui';
import type { ProcessDraftStep } from '@bliks-io/contracts';
import { useCreateStepTemplate } from '@/api/mutations/useStepTemplates';
import { toastApiError } from '@/api/mutations/toast-api-error';

interface DraftEventLike {
  _id: string;
  name?: string | null;
}

interface Props {
  processId: string;
  nodeIds: string[];
  steps: ProcessDraftStep[];
  events: DraftEventLike[];
  onClose: () => void;
}

export function SaveStepTemplateDialog({ processId, nodeIds, steps, events, onClose }: Props) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const createTemplate = useCreateStepTemplate(processId);

  const labels = useMemo(() => {
    const stepsById = new Map(steps.map((s) => [String(s.stableStepId), s]));
    const eventsById = new Map(events.map((e) => [String(e._id), e]));
    return nodeIds.map((id) => {
      const step = stepsById.get(id);
      // Mirrors the canvas (`GatewayNode`): a decision's `task` bucket is
      // leftover text kept across a toggle, never its name.
      if (step?.actionType === 'decision')
        return step.generatedName || t('processFlowEditor.newDecision');
      if (step)
        return (
          step.generatedName ||
          step.task?.taskDescription ||
          t('stepTemplates.saveDialog.unnamedStep')
        );
      const event = eventsById.get(id);
      if (event) return event.name || t('stepTemplates.saveDialog.unnamedEvent');
      return t('stepTemplates.saveDialog.unnamedStep');
    });
  }, [nodeIds, steps, events, t]);

  const handleSave = () => {
    const trimmed = name.trim();
    createTemplate.mutate(
      { name: trimmed, description: description.trim(), nodeIds },
      {
        onSuccess: () => {
          toast.success(t('stepTemplates.saveDialog.saved', { name: trimmed }));
          onClose();
        },
        onError: (error) => toastApiError(error, 'stepTemplates.error')
      }
    );
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>{t('stepTemplates.saveDialog.title')}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <div className="rounded-md border bg-muted/40 px-3 py-2 text-sm">
            <div className="mb-1 text-xs font-medium text-muted-foreground">
              {t('stepTemplates.saveDialog.selectedCount', { count: nodeIds.length })}
            </div>
            <ul className="list-disc pl-4">
              {labels.map((label, index) => (
                <li key={`${nodeIds[index]}`}>{label}</li>
              ))}
            </ul>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="step-template-name">{t('stepTemplates.saveDialog.name')}</Label>
            <Input
              id="step-template-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="step-template-description">
              {t('stepTemplates.saveDialog.description')}
            </Label>
            <Textarea
              id="step-template-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('stepTemplates.saveDialog.cancel')}
          </Button>
          <Button loading={createTemplate.isPending} disabled={!name.trim()} onClick={handleSave}>
            {t('stepTemplates.saveDialog.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
